import type { IdleRates } from '../model/types.ts';

/** This slice has no earned offline-cap unlock, so the server ceiling is 8 hours. */
export const SERVER_OFFLINE_CAP_HOURS = 8;

/** Highest farmable stage. Stage 10 is the Ironhide wall, so it is not a farm stage. */
export const MAX_FARM_STAGE = 9;

const RATE_KEYS = ['goldPerMin', 'xpPerMin', 'dustPerMin', 'dropRollsPerMin'] as const;
export type RateKey = (typeof RATE_KEYS)[number];

/**
 * Per-minute ceiling for one farm stage.
 * Stage 0 farms nothing. The multipliers sit above a one-tick clear of this
 * slice (about 2 seconds per fight) and far below a bigint overflow.
 * commit_loadout must apply this same ceiling after it re-sims the build.
 */
export function offlineRateCap(stageId: number, key: RateKey): number {
  const stage = Math.max(0, Math.min(MAX_FARM_STAGE, Math.floor(stageId)));
  if (stage <= 0) return 0;
  if (key === 'goldPerMin' || key === 'xpPerMin') return stage * 2000;
  if (key === 'dustPerMin') return stage * 200;
  return stage * 60;
}

export function clampOfflineRate(stageId: number, key: RateKey, value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.min(value, offlineRateCap(stageId, key));
}

export function clampOfflineRates(stageId: number, rates: IdleRates): IdleRates {
  return {
    goldPerMin: clampOfflineRate(stageId, 'goldPerMin', rates.goldPerMin),
    xpPerMin: clampOfflineRate(stageId, 'xpPerMin', rates.xpPerMin),
    dustPerMin: clampOfflineRate(stageId, 'dustPerMin', rates.dustPerMin),
    dropRollsPerMin: clampOfflineRate(stageId, 'dropRollsPerMin', rates.dropRollsPerMin),
  };
}
